import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useList } from "@/lib/hooks";
import { Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ArrowRight, Path, Robot, SpinnerGap } from "@phosphor-icons/react";

const inp = "bg-white border-slate-300";

function DeadHeadBox({ dh, origin, setOrigin, busy, onCalc, onZero }) {
  if (busy) return <p className="text-sm text-slate-500 flex items-center gap-2" data-testid="dh-busy"><SpinnerGap size={16} className="animate-spin" />Calculating dead head from the previous delivery…</p>;
  if (!dh) return null;
  if (!dh.need_origin) return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="dh-result">
      <p className="font-semibold text-emerald-800 flex items-center gap-1.5"><Path size={16} />Dead Head: {dh.dead_head_miles} mi</p>
      <p className="text-xs text-emerald-700 mt-0.5">From {dh.from} ({dh.source})</p>
    </div>
  );
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="dh-ask">
      <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5"><Robot size={16} />Asterism AI: {dh.message}</p>
      <div className="flex gap-2">
        <Input value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="e.g. Dallas, TX or last delivery address" className={inp} data-testid="dh-origin-input" />
        <Button onClick={onCalc} disabled={!origin.trim()} className="bg-blue-600 text-white hover:bg-blue-700 shrink-0" data-testid="dh-origin-calc">Calculate</Button>
      </div>
      <button type="button" onClick={onZero} className="text-xs font-semibold text-amber-800 underline" data-testid="dh-zero">Truck is already at the shipper (0 mi)</button>
    </div>
  );
}

export function AssignLoadDialog({ open, onOpenChange, load, carriers, creating, onAssign }) {
  const { data: drivers = [] } = useList("drivers", "/drivers");
  const [carrierId, setCarrierId] = useState("");
  const [driverId, setDriverId] = useState("");
  const [dh, setDh] = useState(null);
  const [origin, setOrigin] = useState("");
  const [busy, setBusy] = useState(false);
  const mine = drivers.filter((d) => d.carrier_id === carrierId);

  useEffect(() => { if (open) { setCarrierId(""); setDriverId(""); setDh(null); setOrigin(""); } }, [open]);

  const calc = async (cid, did, from = "") => {
    if (!cid || !load?.shipper_address) return;
    setBusy(true);
    try { const { data } = await api.post("/ai/deadhead", { carrier_id: cid, driver_id: did, shipper_address: load.shipper_address, origin: from }); setDh(data); }
    catch (e) { setDh({ need_origin: true, message: apiError(e.response?.data?.detail) || "Where is the truck starting from?" }); }
    finally { setBusy(false); }
  };
  const pickCarrier = (v) => { setCarrierId(v); setDriverId(""); setDh(null); };
  const pickDriver = (v) => { setDriverId(v); calc(carrierId, v); };
  const ready = carrierId && driverId && dh && !dh.need_origin && !busy;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 max-w-lg" data-testid="ai-assign-dialog">
        <DialogHeader><DialogTitle className="font-heading">Assign carrier & driver</DialogTitle><DialogDescription className="text-slate-500">Asterism AI works out the Dead Head from the driver's previous delivery to this pickup.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2">
          <Field label="Carrier *">
            <Select value={carrierId} onValueChange={pickCarrier}>
              <SelectTrigger className={inp} data-testid="ai-carrier-select"><SelectValue placeholder="Select carrier" /></SelectTrigger>
              <SelectContent>{carriers.length === 0 ? <SelectItem value="none" disabled>No carriers — add one first</SelectItem> : carriers.map((c) => <SelectItem key={c.id} value={c.id}>{c.carrier_name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {carrierId && (
            <Field label="Driver *">
              <Select value={driverId} onValueChange={pickDriver}>
                <SelectTrigger className={inp} data-testid="ai-driver-select"><SelectValue placeholder={mine.length ? "Select driver" : "No drivers for this carrier"} /></SelectTrigger>
                <SelectContent>{mine.length === 0 ? <SelectItem value="none" disabled>Add drivers in Driver Info first</SelectItem> : mine.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`ai-driver-opt-${d.id}`}>{d.name}{d.truck ? ` · Truck ${d.truck}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          {driverId && <DeadHeadBox dh={dh} origin={origin} setOrigin={setOrigin} busy={busy} onCalc={() => calc(carrierId, driverId, origin)}
            onZero={() => setDh({ need_origin: false, dead_head_miles: 0, from: "the shipper", source: "entered by agent" })} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-300 bg-white text-slate-700">Cancel</Button>
          <Button onClick={() => onAssign({ carrierId, driverId, deadHead: dh.dead_head_miles, from: dh.from })} disabled={creating || !ready} className="bg-emerald-600 text-white hover:bg-emerald-700 font-semibold" data-testid="ai-confirm-create">{creating ? "Assigning…" : "Assign Load"}<ArrowRight size={16} className="ml-1.5" /></Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
